import React from 'react';
import { Empty, Input, Tabs, Tree } from 'antd';

const { Search } = Input;
const { TabPane } = Tabs;
const { TreeNode } = Tree;

const OWL_OBJECT_PROPERTY = 'http://www.w3.org/2002/07/owl#ObjectProperty';
const OWL_DATATYPE_PROPERTY = 'http://www.w3.org/2002/07/owl#DatatypeProperty';
const OWL_ANNOTATION_PROPERTY = 'http://www.w3.org/2002/07/owl#AnnotationProperty';

const getLabel = (details, lang) => {
  if (!details) return '';
  const labels = Array.isArray(details.labels) ? details.labels : [];
  const langLabel = labels.find(label => label.lang === lang);
  if (langLabel) return langLabel.value;
  const emptyLabel = labels.find(label => !label.lang);
  if (emptyLabel) return emptyLabel.value;
  if (labels.length > 0) return labels[0].value;
  return details.label || details.iri;
};

const hasType = (node, type) => {
  const types = node && node.details && Array.isArray(node.details.types) ? node.details.types : [];
  return types.indexOf(type) >= 0;
};

const filterTreeByType = (nodes, type) => {
  return (nodes || [])
    .map(node => {
      const children = filterTreeByType(node.children || [], type);
      if (!hasType(node, type) && children.length < 1) return null;
      return {...node, children};
    })
    .filter(Boolean);
};

const filterTreeByText = (nodes, text, lang) => {
  const pattern = (text || '').trim().toLowerCase();
  if (!pattern) return nodes || [];

  return (nodes || [])
    .map(node => {
      const title = getLabel(node.details, lang).toLowerCase();
      const iri = (node.iri || '').toLowerCase();
      const children = filterTreeByText(node.children || [], text, lang);
      if (title.indexOf(pattern) < 0 && iri.indexOf(pattern) < 0 && children.length < 1) return null;
      return {...node, children};
    })
    .filter(Boolean);
};

const findPath = (nodes, iri, path = []) => {
  for (let i = 0; i < (nodes || []).length; i++) {
    const node = nodes[i];
    const currentPath = path.concat(node.iri);
    if (node.iri === iri) return currentPath;
    const childPath = findPath(node.children || [], iri, currentPath);
    if (childPath.length > 0) return childPath;
  }
  return [];
};

const renderTreeNodes = (nodes, lang) => {
  return (nodes || []).map(node => {
    const title = (
      <span title={node.iri}>
        {getLabel(node.details, lang)}
      </span>
    );

    if (node.children && node.children.length > 0) {
      return (
        <TreeNode title={title} key={node.iri} dataRef={node}>
          {renderTreeNodes(node.children, lang)}
        </TreeNode>
      );
    }

    return <TreeNode title={title} key={node.iri} dataRef={node} />;
  });
};

class OntologyTree extends React.Component {
  state = {
    expandedKeys: [],
    search: '',
  };

  componentDidMount() {
    this.syncSelectedPath();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedIRI !== this.props.selectedIRI || prevProps.nodes !== this.props.nodes) {
      this.syncSelectedPath();
    }
  }

  syncSelectedPath() {
    const { nodes, selectedIRI } = this.props;
    if (!selectedIRI) return;
    const path = findPath(nodes, selectedIRI);
    if (path.length < 1) return;
    this.setState({expandedKeys: Array.from(new Set(this.state.expandedKeys.concat(path)))});
  }

  onSelect = (selectedKeys) => {
    const iri = selectedKeys && selectedKeys.length > 0 ? selectedKeys[0] : undefined;
    if (iri && this.props.onSelect)
      this.props.onSelect(iri);
  };

  render() {
    const { nodes, selectedIRI, lang } = this.props;
    const filteredNodes = filterTreeByText(nodes, this.state.search, lang);

    return (
      <div>
        <Search
          allowClear
          value={this.state.search}
          onChange={event => this.setState({search: event.target.value})}
          style={{marginBottom: 8}}
        />
        {filteredNodes.length < 1 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Tree
            selectedKeys={selectedIRI ? [selectedIRI] : []}
            expandedKeys={this.state.expandedKeys}
            onExpand={expandedKeys => this.setState({expandedKeys})}
            onSelect={this.onSelect}
          >
            {renderTreeNodes(filteredNodes, lang)}
          </Tree>
        )}
      </div>
    );
  }
}

export const ClassTree = (props) => (
  <OntologyTree {...props} nodes={props.classTree || []} />
);

export const ObjectPropertyTree = (props) => (
  <OntologyTree {...props} nodes={filterTreeByType(props.propertyTree || [], OWL_OBJECT_PROPERTY)} />
);

export const DatatypePropertyTree = (props) => (
  <OntologyTree {...props} nodes={filterTreeByType(props.propertyTree || [], OWL_DATATYPE_PROPERTY)} />
);

export const AnnotationPropertyTree = (props) => (
  <OntologyTree {...props} nodes={filterTreeByType(props.propertyTree || [], OWL_ANNOTATION_PROPERTY)} />
);

class OntologyNavigator extends React.Component {
  render() {
    const { classTree, propertyTree, selectedIRI, lang, onSelect } = this.props;
    const treeProps = {classTree, propertyTree, selectedIRI, lang, onSelect};

    return (
      <Tabs defaultActiveKey="classes" type="card">
        <TabPane tab="Classes" key="classes">
          <ClassTree {...treeProps} />
        </TabPane>
        <TabPane tab="Object properties" key="objectProperties">
          <ObjectPropertyTree {...treeProps} />
        </TabPane>
        <TabPane tab="Data properties" key="datatypeProperties">
          <DatatypePropertyTree {...treeProps} />
        </TabPane>
        <TabPane tab="Annotation properties" key="annotationProperties">
          <AnnotationPropertyTree {...treeProps} />
        </TabPane>
      </Tabs>
    );
  }
}

export default OntologyNavigator;
